import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    Paper,
    Chip,
    FormControl,
    InputAdornment,
    TextField,
    Select,
    MenuItem,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    IconButton,
    Menu as MuiMenu,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Search, MoreVert, Edit, Delete, ViewColumn } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { deleteProduct, getProductsByCategoryId } from "../../Apis/ProductAPI";

const AdminProductsList = () => {
    const location = useLocation();
    const id = location.state?.categoryId;
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    useEffect(() => {
        fetchProducts();
    }, [id, page, search, statusFilter, rowsPerPage]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const query = {
                limit: rowsPerPage,
                page,
                search: search || undefined,
                status: statusFilter || undefined,
            };
            const response = await getProductsByCategoryId(id, query);
            setProducts(response?.products || response?.data || []);
            setTotalPages(response?.totalPages || 1);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const normalizeStatus = (val) =>
        val === true || val === "true" || val === 1 || val === "1";
    const getStatusLabel = (p) =>
        normalizeStatus(p.status) ? "Active" : "Inactive";

    // Action menu handlers
    const handleMenuOpen = (event, product) => {
        setAnchorEl(event.currentTarget);
        setSelectedProduct(product);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedProduct(null);
    };
    const handleEdit = () => {
        navigate(`/adminproducts/edit/${selectedProduct.id}`, {
            state: { product: selectedProduct, id },
        });
        handleMenuClose();
    };

    const handleDeleteClick = () => setDeleteDialogOpen(true);

    const handleDeleteConfirm = async () => {
        try {
            await deleteProduct(selectedProduct.id);
            // Optimistically update without extra GET
            setProducts((prev) =>
                prev.filter((p) => p.id !== selectedProduct.id)
            );
            setSnackbar({
                open: true,
                message: "Product deleted successfully!",
                severity: "success",
            });
        } catch {
            setSnackbar({
                open: true,
                message: "Failed to delete product.",
                severity: "error",
            });
        } finally {
            setDeleteDialogOpen(false);
            setSelectedProduct(null);
            handleMenuClose();
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedProduct(null);
    };

    const handleTabs = () => {
        navigate(`/getproducttabs/${selectedProduct.id}`);
        handleMenuClose();
    };

    return (
        <Box sx={{ padding: 3, width: "95%" }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                }}
            >
                <Typography variant="h5" fontWeight="bold">
                    Products
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate("/adminproducts/add", { state: { id } })}
                >
                    + Add Product
                </Button>
            </Box>

            {/* Filters */}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 2,
                    alignItems: "center",
                }}
            >
                <TextField
                    placeholder="Search products"
                    size="small"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
                    <Button
                        variant="contained"
                        sx={{ width: 100 }}
                        onClick={() => {
                            setSearch(searchText);
                            setPage(1);
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{ width: 100 }}
                        onClick={() => {
                            setSearch("");
                            setSearchText("");
                            setStatusFilter("");
                            setRowsPerPage(10);
                            setPage(1);
                        }}
                    >
                        Reset
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            {loading ? (
                <Box sx={{ textAlign: "center", py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Image</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.length > 0 ? (
                                products.map((prod) => (
                                    <TableRow key={prod.id}>
                                        <TableCell>
                                            <img
                                                src={prod.imageUrl || "/placeholder.png"}
                                                alt={prod.name}
                                                width={60}
                                                height={60}
                                                style={{ objectFit: "cover", borderRadius: 4 }}
                                            />
                                        </TableCell>
                                        <TableCell>{prod.name}</TableCell>
                                        <TableCell>{prod.price}</TableCell>
                                        <TableCell>
                                            {prod.quantity} {prod.quantity_type}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusLabel(prod)}
                                                color={
                                                    normalizeStatus(prod.status) ? "success" : "error"
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton onClick={(e) => handleMenuOpen(e, prod)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No products found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                }}
            >
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value));
                            setPage(1);
                        }}
                    >
                        {[5, 10, 20, 50].map((num) => (
                            <MenuItem key={num} value={num}>
                                {num} Rows
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, val) => setPage(val)}
                />
            </Box>

            {/* Action Menu */}
            <MuiMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDeleteClick}>
                    <ListItemIcon>
                        <Delete fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: "error" }}>
                        Delete
                    </ListItemText>
                </MenuItem>
                <MenuItem onClick={handleTabs}>
                    <ListItemIcon>
                        <ViewColumn fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Tabs</ListItemText>
                </MenuItem>
            </MuiMenu>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete product "{selectedProduct?.name}"?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MuiAlert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default AdminProductsList;
